import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../components/common/PageHeader';
import { FaChartLine, FaShieldAlt, FaUserTie, FaGlobeAmericas } from 'react-icons/fa';

const AboutUsPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'About Us' }
  ];

  const values = [
    {
      icon: <FaChartLine className="w-10 h-10 text-secondary" />,
      title: "Expert Investment",
      description: "Our team of experts brings together years of experience in traditional finance, blockchain technology, and investment management."
    },
    {
      icon: <FaShieldAlt className="w-10 h-10 text-secondary" />,
      title: "Transparency & Security",
      description: "We prioritize transparency in all our operations, providing clients with unparalleled insight into their investments."
    },
    {
      icon: <FaUserTie className="w-10 h-10 text-secondary" />,
      title: "Client-Centered Approach",
      description: "We take the time to understand your ambitions and create unique solutions for every stage of your life's journey."
    },
    {
      icon: <FaGlobeAmericas className="w-10 h-10 text-secondary" />,
      title: "Global Presence",
      description: "With presence in Spain, Portugal, Luxembourg, Turkey, Mexico, Peru, Colombia and Argentina, we offer truly international investment solutions."
    }
  ];

  const stats = [
    { value: "15+", label: "Years of Excellence" },
    { value: "$5B+", label: "Assets Managed" },
    { value: "700+", label: "Team Members" },
    { value: "150+", label: "Investment Professionals" }
  ];

  return (
    <>
      <PageHeader 
        title="About Us" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/business-colleagues-banner-concept-with-copy-space.jpg"
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/business-team-working-together.jpg" 
                alt="Segway Capital Team" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-heading font-bold text-primary">We are Segway Capital</h2>

              <p className="text-lg text-gray-700">
                Segway Capital is a pioneering blockchain investment program that is revolutionizing the way we approach finance. Using cutting-edge blockchain technology, we are transforming the investment landscape by providing our clients with unparalleled transparency, security, and efficiency.
              </p>

              <p className="text-lg text-gray-700">
                Our team of experts brings together years of experience in traditional finance, blockchain technology, and investment management. We offer a diverse range of products and services designed to help you achieve your financial goals, whatever they may be.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 pt-4">
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} className="text-center bg-white p-6 rounded-lg shadow-sm flex-1">
                    <div className="text-4xl font-heading font-bold text-secondary mb-2">{stat.value}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-6">
                {stats.slice(2, 4).map((stat, index) => (
                  <div key={index} className="text-center bg-white p-6 rounded-lg shadow-sm flex-1">
                    <div className="text-4xl font-heading font-bold text-secondary mb-2">{stat.value}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-heading font-bold mb-6">Our Core Values</h2>
            <p className="text-lg text-gray-300">
              At Segway Capital, we adhere to a set of core values that guide everything we do. These principles form the foundation of our approach to investment and client relationships.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:bg-white/15 transition-all duration-300"
              >
                <div className="mb-6">{value.icon}</div>
                <h3 className="text-2xl font-heading font-semibold mb-4">{value.title}</h3>
                <p className="text-gray-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1 space-y-6"
            >
              <h2 className="text-4xl font-heading font-bold text-primary">Our Approach</h2>

              <p className="text-lg text-gray-700">
                We work with a team of highly experienced individuals to deliver superior asset management solutions to a diverse range of clients. For us, trust is key. Therefore, we consistently adhere to our fiduciary obligation and apply due diligence in executing any investment decision.
              </p>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-secondary">
                  <h3 className="text-xl font-heading font-semibold mb-2 text-primary">Wealth Management Solutions</h3>
                  <p className="text-gray-600">
                    Segway Capital Management offers a personal and proactive approach to managing your wealth. We take the time to get to know you and understand your ambitions to create unique solutions for every stage of your life's journey.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-secondary">
                  <h3 className="text-xl font-heading font-semibold mb-2 text-primary">Investment Advisory</h3>
                  <p className="text-gray-600">
                    Our Investment Advisory services are tailored to help you make informed decisions about your investment strategy. We provide expert guidance and personalized recommendations based on your financial goals, risk tolerance, and market conditions.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <img 
                src="/assets/co-workers-discussing-business-project.jpg" 
                alt="Our Approach" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-heading font-bold text-primary mb-6">Our Global Presence</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              With a local presence in multiple countries, our global footprint allows us to provide specialized services to clients worldwide while understanding local market dynamics.
            </p>
          </motion.div>

          <div className="bg-white rounded-xl shadow-custom p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-heading font-semibold mb-4 text-primary border-b border-gray-200 pb-3">Europe</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>Spain</li>
                  <li>Portugal</li>
                  <li>Luxembourg</li>
                </ul>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-heading font-semibold mb-4 text-primary border-b border-gray-200 pb-3">Latin America</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>Mexico</li>
                  <li>Peru</li>
                  <li>Colombia</li>
                  <li>Argentina</li>
                </ul>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-heading font-semibold mb-4 text-primary border-b border-gray-200 pb-3">Middle East</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>Turkey</li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* <section className="py-16 md:py-20 bg-primary text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-heading font-bold mb-6">Risk Disclosure</h2>
            <p className="text-lg text-white/90 mb-0">
              An investment in the funds managed by SEGWAY is speculative and involves a high degree of risk, including the risk that these funds could lose value (a risk of loss). SEGWAY will employ certain trading techniques, such as short selling and the use of leverage, which may increase the risk of investment loss.
            </p>
          </motion.div>
        </div>
      </section> */}
    </>
  );
};

export default AboutUsPage; 